/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { message, Table, Tag, Button, Empty, Spin } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { reservationsApi } from "../../apis";

// ─────────────────────────────────────────────────────────────────────────────
const MyTickets = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchMyTickets();
  }, [filters]);

  const fetchMyTickets = async () => {
    try {
      setLoading(true);
      const res = await reservationsApi.getMy({
        page: filters.page,
        limit: filters.limit,
      });

      // Surface every reservation as a ticket row.
      // Include reservations without tickets (pending/cancelled) so the customer
      // can see their full history, not just issued tickets.
      const rows = (res.data || []).map((r) => ({
        // ticket fields (may be null)
        id: r.ticket?.id || `no-ticket-${r.id}`,
        ticketNumber: r.ticket?.ticketNumber || null,
        isCheckedIn: r.ticket?.isCheckedIn || false,
        isCancelled: r.ticket?.isCancelled || false,
        checkedInAt: r.ticket?.checkedInAt || null,
        // parent reservation
        reservation: r,
      }));

      setTickets(rows);
      setTotal(res.total || rows.length);
    } catch {
      message.error("Failed to load your tickets");
    } finally {
      setLoading(false);
    }
  };

  // ── Status helper ─────────────────────────────────────────────────────────
  const statusTag = (r) => {
    const rs = r.reservation?.status;

    if (r.isCheckedIn)
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          Checked In
        </Tag>
      );

    if (r.isCancelled || rs === "cancelled")
      return (
        <Tag color="red" icon={<StopOutlined />}>
          Cancelled
        </Tag>
      );

    if (rs === "refunded")
      return (
        <Tag color="orange" icon={<StopOutlined />}>
          Refunded
        </Tag>
      );

    if (rs === "pending" || !r.ticketNumber)
      return (
        <Tag color="default" icon={<ClockCircleOutlined />}>
          Awaiting Payment
        </Tag>
      );

    return (
      <Tag color="volcano" icon={<ClockCircleOutlined />}>
        Active
      </Tag>
    );
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Ticket #",
      key: "ticketNumber",
      render: (_, r) => {
        if (!r.ticketNumber) {
          return (
            <span style={{ color: "#d1d5db", fontSize: 12 }}>
              Not issued yet
            </span>
          );
        }
        const isInvalid =
          r.isCancelled ||
          r.reservation?.status === "cancelled" ||
          r.reservation?.status === "refunded";
        return (
          <code
            style={{
              background: isInvalid ? "#f3f4f6" : "#f0f4ff",
              padding: "2px 10px",
              borderRadius: 6,
              color: isInvalid ? "#9ca3af" : "#7B4D12",
              fontWeight: 700,
              fontSize: 13,
              textDecoration: isInvalid ? "line-through" : "none",
            }}
          >
            {r.ticketNumber}
          </code>
        );
      },
    },
    {
      title: "Trip",
      key: "trip",
      render: (_, r) => (
        <div>
          <strong>{r.reservation?.tripDate?.trip?.name || "—"}</strong>
          <div className="small text-muted">
            {r.reservation?.tripDate?.date || "—"}
          </div>
        </div>
      ),
    },
    {
      title: "Time",
      key: "time",
      render: (_, r) => {
        const t = r.reservation?.tripDate?.trip;
        return t ? `${t.startTime} – ${t.endTime}` : "—";
      },
    },
    {
      title: "Deck",
      key: "deck",
      render: (_, r) => r.reservation?.deck?.name || "—",
    },
    {
      title: "Seats",
      key: "seats",
      align: "center",
      render: (_, r) => r.reservation?.seats || "—",
    },
    {
      title: "Amount",
      key: "total",
      render: (_, r) =>
        r.reservation?.totalPrice
          ? `EGP ${Number(r.reservation.totalPrice).toLocaleString()}`
          : "—",
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => statusTag(r),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => history.push(`/reservations/${r.reservation?.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>My Tickets</h5>
                </div>
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <Spin size="large" />
                </div>
              ) : tickets.length === 0 ? (
                <Empty
                  description="You have no tickets yet"
                  style={{ padding: "60px 0" }}
                >
                  <Button
                    type="primary" style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
                    onClick={() => history.push("/reservations/add")}
                  >
                    Book a Trip
                  </Button>
                </Empty>
              ) : (
                <Table
                  columns={columns}
                  dataSource={tickets}
                  rowKey="id"
                  loading={loading}
                  rowClassName={(r) =>
                    r.isCancelled ||
                    r.reservation?.status === "cancelled" ||
                    r.reservation?.status === "refunded"
                      ? "opacity-50"
                      : ""
                  }
                  pagination={{
                    current: filters.page,
                    pageSize: filters.limit,
                    total,
                    showSizeChanger: true,
                    showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} tickets`,
                  }}
                  onChange={(p) =>
                    setFilters((f) => ({
                      ...f,
                      page: p.current,
                      limit: p.pageSize,
                    }))
                  }
                  scroll={{ x: true }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyTickets;
